import type { Plugin } from '@nuxt/types';
import type { ScriptType, ThirdPartyScript, NuxtThirdPartyPlugin, ScriptMap, Entry } from './types';
import { waitFor } from './utils';
import { delayTime, DEFAULT_DELAY } from './constants';
import tiktok from './tiktok/tiktok';
import fullstory from './fullstory/fullstory';
import quora from './quora/quora';
import facebook from './facebook/facebook';
import linkedin from './linkedin/linkedin';
import userleap from './userleap/index';
import { onLoad } from '~/utils/onLoad';

const debug = (message: string): void => {
  // eslint-disable-next-line
  if (!process.env.isProduction) console.log(message);
};

type ScriptsForLoading = Partial<Record<ScriptType, ThirdPartyScript>>;
/**
 * As a big application we use many 3rd party scripts
 * But that affects our performance as well
 * That Plugin:
 * - loads scripts with delay on app load;
 * - inject scripts object to ctx.$3rdParty (eg $3rdParty.facebook);
 * - provides immediately load as a promise;
 *
 * Every script should be prepared for plugin:
 * 1. Better to separate script for 2 parts:
 *    - setup: with queue and object initialization to have earliest access to script as possible;
 *    - load: loading/injection of a script; If script only have load function that's ok.
 * 2. Patch load function:
 *    We must provide onLoad/onError callback to injection (see any implemented script)
 * 3. If script starts to load dependencies you must provide isInitialized Promise, which must check
 *    for some initialize properties of script in a loop.
 * 4. Return functions above
 */
const ThirdPartyPlugin: Plugin = (ctx, inject) => {
  const scripts: ScriptsForLoading = {
    tiktok: tiktok(ctx),
    facebook: facebook(ctx),
    quora: quora(ctx),
    userleap: userleap(ctx),
    linkedin: linkedin(ctx),
    ...(process.env.isProduction && { fullstory: fullstory(ctx) }),
  };

  /**
   * Load all 3rd party scripts with delay on application start
   */
  const loadAllWithDelay = () => {
    Object.entries(scripts).forEach(([name, script]) => {
      const delay = delayTime[name as ScriptType] || DEFAULT_DELAY;
      onLoad(() => {
        if (!script || script.isLoaded === true) return;
        script.isLoaded = true;
        script
          .load()
          .then(() => {
            debug(`Script '${name}' loaded with delay`);
          })
          .catch(() => {
            debug(`Script '${name}' fails with delay`);
          });
      }, delay);
    });
  };
  loadAllWithDelay();

  /**
   * Load scripts immediately
   */
  const loadImmediately: NuxtThirdPartyPlugin['loadImmediately'] = async (scriptName, timeout = 3000) => {
    const scriptNames = Array.isArray(scriptName) ? scriptName : [scriptName];

    // 1. Load scripts with pathched onLoad/onError
    //    Some scripts could fail because of adblock or network
    const scriptsForLoad = scriptNames.map(name => {
      const script = scripts[name];
      if (script?.isLoaded) return null;

      return script!
        .load()
        .then(() => {
          debug(`Script '${name}' starts loading immediately`);
          script!.isLoaded = true;
        })
        .catch(() => {
          debug(`Script '${name}' fails`);
          script!.isError = true;
        });
    });

    // 2. Wait until complete of p.1 + timeout limit
    //    We can't wait forever in case if some script won't load
    const startTime = performance.now();
    await Promise.race([Promise.all(scriptsForLoad), waitFor(timeout)]);

    // 3. Check properties of initialized script
    //    Some scripts could start to fetch dependencies, check inner state
    //    of initialiation. In most cases it could be overwhelm.
    const scriptsForInitialize = scriptNames.map(name => {
      const script = scripts[name];
      if (script?.isError) return null;
      if (!script?.isInitialized) {
        debug(`Script '${name}' doesn't have init checker, perhaps loaded`);
        return null;
      }
      return script.isInitialized().then(() => {
        debug(`Script '${name}' loaded immediately`);
      });
    });

    // 4. Wait until complete p.3 + timeout
    const restTimeout = timeout - (performance.now() - startTime);
    await Promise.race([Promise.all(scriptsForInitialize), waitFor(restTimeout)]);

    // Just for sure that script managed to do job
    await waitFor(200);
  };

  /**
   * Collect all entries for scripts and inject by name
   */
  const scriptEntries = Object.entries(scripts).reduce((entries: Record<string, Entry>, [name, script]) => {
    if (script?.entry) entries[name] = script.entry;
    return entries;
  }, {}) as ScriptMap;

  inject('3rdParty', {
    loadImmediately,
    ...scriptEntries,
  });
};

export default ThirdPartyPlugin;
